import type React from "react"
import { useState, useEffect } from "react"
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
} from "@mui/material"
import { useParams, useNavigate } from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import RequestDetail from "../../../components/crm/request/RequestDetail"
import { requestService } from "../../../services/CrmRequestService"
import { useAuth } from "../../../context/AuthContext"

const RequestDetailPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>()
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const [userProfileId, setUserProfileId] = useState<number | null>(null)
  const [userRole, setUserRole] = useState<string>("")
  const { userToken } = useAuth();

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  useEffect(() => {
    const profiles = userToken?.user.profiles || []
    if (profiles && profiles.length > 0) {
      const activeProfile = profiles.find(profile => profile.roleId === 3 || profile.roleId === 7)
      if (activeProfile) {
        setUserProfileId(activeProfile.id)
        setUserRole(activeProfile.roleName || "")
      }
    }
  }, [userToken?.user.profiles])

  useEffect(() => {
    const loadRequestsIfNeeded = async () => {
      if (!requestId || isNaN(Number(requestId))) {
        setError("ID de solicitud no válido")
        setLoading(false)
        return
      }

      if (!userProfileId || !userRole) {
        return
      }

      try {
        setLoading(true)

        if (userRole === "Broker") {
          await requestService.getRequestsByBroker(userProfileId)
        } else if (userRole === "Cliente") {
          await requestService.getRequestsByClient(userProfileId)
        }

        setLoading(false)
      } catch (err: any) {
        console.error("Error al cargar las solicitudes:", err)
        setError(err.message || "Error al cargar los datos de la solicitud")
        setLoading(false)
      }
    }

    loadRequestsIfNeeded()
  }, [requestId, userProfileId, userRole])

  const handleStatusChange = () => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
    }, 500)
  }

  const handleBack = () => {
    navigate(-1)
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            size={isMobile ? "small" : "medium"}
          >
            Volver a Solicitudes
          </Button>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        elevation={isMobile ? 1 : 2}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: { xs: 1, md: 2 },
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          alignItems={isMobile ? "flex-start" : "center"}
          mb={isMobile ? 1 : 3}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 2 : 0 }}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
          >
            Volver
          </Button>

          {isMobile && <Divider sx={{ width: "100%", my: 2 }} />}

          <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 600 }}>
            Detalles de Solicitud
          </Typography>
        </Box>

        {requestId && <RequestDetail requestId={Number(requestId)} onStatusChange={handleStatusChange} />}
      </Paper>
    </Container>
  )
}

export default RequestDetailPage
